import { createSelector } from "reselect";
import { ComplexValueParameterType, CustomVariableRelativeValueVariableType, RecipeDialogType, RecipeStepType, } from "./recipe.types";
import { isNotEmpty } from "utils/validate";
import { checkRecipeParameterIsCompleted, flatSteps, getCustomVariablesUsingComplexValue, getLiquidsReferencedInComplexValues, getRecipeStepsUsingComplexValue, getRecipeStepsUsingGas, getRecipeStepsUsingLiquids, referencedStepMapper, } from "./recipe.utils";
import { checkIfFormulaReferencingCustomVariable } from "./custom-variables.utils";
import { bioreactorParametersOptionsSelector, processVariablesOptionsSelector, } from "store/global/global.selectors";
import { RemovableEntityType } from "store/knowledge-base-item/knowledge-base-item.types";
export const getRecipeState = (state) => state.recipe;
export const getRecipeStateSelector = createSelector(getRecipeState, (state) => state.state);
export const recipeLiquidsSelector = createSelector(getRecipeStateSelector, (state) => state.liquids);
export const displayModeSelector = createSelector(getRecipeState, (state) => state.mode);
export const recipeBuilderActionMode = createSelector(getRecipeState, (state) => state.recipeBuilderActionMode);
export const plusIconPlacementSelector = createSelector(getRecipeState, (state) => state.plusIconPlacement);
export const stepsListSelector = createSelector(getRecipeState, (state) => state.stepsList);
export const selectedStepIdsSelector = createSelector(getRecipeState, (state) => state.selectedStepIds);
export const recipeStepsSelector = createSelector(getRecipeState, (state) => state.state.steps);
export const recipeCustomVariablesSelector = createSelector(getRecipeState, (state) => state.state.customVariables);
export const currentRecipeCustomVariablesSelector = createSelector(recipeCustomVariablesSelector, (customVariables) => [...customVariables].sort((a, b) => new Date(b.lastSaved).getTime() - new Date(a.lastSaved).getTime()));
export const configuredCustomVariablesListSelector = createSelector(currentRecipeCustomVariablesSelector, (customVariables) => customVariables.filter(({ formula }) => !!formula?.complete));
export const recipeCustomVariableDefaultNameSelector = createSelector(getRecipeState, (state) => state.customVariableDefaultName);
export const customVariableCurrentFormulaStatusSelector = createSelector(getRecipeState, (state) => state.currentCustomVariableFormulaStatus);
export const flatStepsSelector = createSelector(getRecipeState, (state) => flatSteps(state.state.steps));
export const stepsReferencedToLiquidSelector = (liquidId) => createSelector(flatStepsSelector, (steps) => getRecipeStepsUsingLiquids(steps, liquidId));
export const stepsReferencedToGasSelector = (gasId) => createSelector(flatStepsSelector, (steps) => getRecipeStepsUsingGas(steps, gasId));
export const stepsReferencedToParameterSelector = (parameterId) => createSelector(flatStepsSelector, (steps) => {
    if (!isNotEmpty(parameterId)) {
        return [];
    }
    return getRecipeStepsUsingComplexValue(steps, ComplexValueParameterType.PARAMETER, parameterId);
});
export const liquidsReferencedToParameterSelector = (parameterId) => createSelector(recipeLiquidsSelector, (liquids) => getLiquidsReferencedInComplexValues(liquids, ComplexValueParameterType.PARAMETER, parameterId));
export const customVariablesReferencedToParameterSelector = (parameterId) => createSelector(getRecipeStateSelector, (state) => getCustomVariablesUsingComplexValue(state.customVariables, parameterId));
export const isInUseSelector = createSelector(getRecipeState, (state) => state.inUse);
export const alternativeStepActiveFlowsSelector = createSelector(getRecipeState, (state) => state.alternativeStepActiveFlows);
export const getAlternativeStepActiveFlow = (stepId) => createSelector(alternativeStepActiveFlowsSelector, (activeFlows) => activeFlows[stepId]);
export const getRecipeDialogState = (dialogType) => createSelector(getRecipeState, (state) => state.dialogs[dialogType]);
export const alternativeStepNamesSelector = createSelector(getRecipeState, (state) => {
    const currentRecipeNames = flatSteps(state.state.steps, ({ type }) => type === RecipeStepType.ALTERNATIVE).map((step) => step.name);
    return state.alternativeStepNames
        .filter((name) => !currentRecipeNames.includes(name))
        .map((value) => ({ value, label: value }));
});
export const gasNamesSelector = createSelector(getRecipeState, (state) => {
    const currentRecipeNames = state.state.gases.map(({ name }) => name);
    return state.gasNames
        .filter((name) => !currentRecipeNames.includes(name))
        .map((value) => ({ value, label: value }));
});
export const liquidNamesSelector = createSelector(getRecipeState, (state) => {
    const currentRecipeNames = state.state.liquids.map(({ name }) => name);
    return state.liquidNames
        .filter((name) => !currentRecipeNames.includes(name))
        .map((value) => ({ value, label: value }));
});
export const completedParametersSelector = createSelector(getRecipeStateSelector, (state) => state.parameters.filter(checkRecipeParameterIsCompleted));
export const parameterNamesSelector = createSelector(getRecipeState, (state) => {
    const currentRecipeNames = state.state.parameters.map(({ name }) => name);
    return state.parameterNames
        .filter((name) => !currentRecipeNames.includes(name))
        .map((value) => ({ value, label: value }));
});
export const editableParameterSelector = createSelector(getRecipeState, (state) => state.editableParameter);
export const currentCustomVariableSelector = createSelector(currentRecipeCustomVariablesSelector, getRecipeDialogState(RecipeDialogType.CUSTOM_VARIABLE), (customVariables, dialogState) => customVariables.find((item) => item.id === dialogState.id));
export const customVariableRelativeVariableOptionsSelector = createSelector(processVariablesOptionsSelector, currentRecipeCustomVariablesSelector, (processVariables, customVariables) => {
    const base = processVariables.reduce((acc, option) => option.category === "RecipeVariables"
        ? acc
        : [
            ...acc,
            {
                ...option,
                value: {
                    value: option.value,
                    type: CustomVariableRelativeValueVariableType.ProcessVariable,
                    units: option.units,
                },
            },
        ], []);
    const customVariablesOptions = customVariables.map(({ id, name, formula }) => ({
        value: {
            value: id,
            type: CustomVariableRelativeValueVariableType.CustomVariable,
            units: !!formula?.configuredUnits && !!formula?.configuredUnits?.isSuccess
                ? formula?.configuredUnits?.complexValue
                : "",
        },
        label: name,
        isGroupName: false,
    }));
    return [
        ...base,
        {
            value: {
                value: CustomVariableRelativeValueVariableType.CustomVariable,
            },
            label: "Custom Variables",
            isGroupName: true,
            disabled: true,
        },
        ...customVariablesOptions,
    ];
});
export const customVariableRelativeParameterOptionsSelector = createSelector(completedParametersSelector, bioreactorParametersOptionsSelector, (recipeParameters, bioreactorParameters) => {
    const recipeParametersOptions = recipeParameters.map(({ id, name, units }) => ({
        label: name,
        value: {
            value: id,
            type: CustomVariableRelativeValueVariableType.RecipeParameter,
            units,
        },
    }));
    const bioreactorParameterOptions = bioreactorParameters.map(({ value, label, defaultUnit }) => ({
        value: {
            value,
            type: CustomVariableRelativeValueVariableType.BioreactorParameter,
            units: defaultUnit,
        },
        label,
        isGroupName: false,
    }));
    return [
        {
            value: "Bioreactor parameters",
            label: "Bioreactor parameters",
            isGroupName: true,
            disabled: true,
        },
        ...bioreactorParameterOptions,
        {
            value: "Recipe parameters",
            label: "Recipe parameters",
            isGroupName: true,
            disabled: true,
        },
        ...recipeParametersOptions,
    ];
});
const customVariableTransformationsSelector = createSelector(getRecipeState, (state) => state.customVariableTransformations);
export const customVariableTransformationOptionsSelector = createSelector(customVariableTransformationsSelector, (transformations) => transformations.map(({ name, operationType }) => ({ value: operationType, label: name })));
export const customVariableReferencesDataSelector = (customVariableId) => createSelector(currentRecipeCustomVariablesSelector, flatStepsSelector, recipeLiquidsSelector, (customVariables, stepsList, liquidsList) => {
    const customVariablesList = customVariables.filter(({ formula }) => checkIfFormulaReferencingCustomVariable(formula, customVariableId));
    const recipeSteps = getRecipeStepsUsingComplexValue(stepsList, ComplexValueParameterType.CUSTOM_VARIABLE, customVariableId);
    const liquidsCount = getLiquidsReferencedInComplexValues(liquidsList, ComplexValueParameterType.CUSTOM_VARIABLE, customVariableId)?.length;
    const hasReferences = customVariablesList.length > 0 || recipeSteps.length > 0 || liquidsCount > 0;
    return { customVariablesCount: customVariablesList.length, recipeSteps, liquidsCount, hasReferences };
});
export const referencesSelector = (deleteTarget) => createSelector(stepsListSelector, flatStepsSelector, recipeLiquidsSelector, recipeCustomVariablesSelector, (stepsList, steps, liquids, customVariables) => {
    if (deleteTarget === null) {
        return [];
    }
    const { id, type } = deleteTarget;
    switch (type) {
        case RemovableEntityType.RECIPE_PARAMETER:
            return [
                ...getRecipeStepsUsingComplexValue(steps, ComplexValueParameterType.PARAMETER, id).map((step) => referencedStepMapper(step, stepsList)),
                ...getLiquidsReferencedInComplexValues(liquids, ComplexValueParameterType.PARAMETER, id).map(({ id, name }) => ({
                    name: name ?? "",
                    type: RecipeDialogType.LIQUID,
                    payload: { isOpen: true, id },
                })),
                ...getCustomVariablesUsingComplexValue(customVariables, id).map(({ name, id }) => ({
                    name: name ?? "",
                    type: RecipeDialogType.CUSTOM_VARIABLE,
                    payload: { isOpen: true, id },
                })),
            ];
        case RemovableEntityType.RECIPE_CUSTOM_VARIABLE:
            return [
                ...getRecipeStepsUsingComplexValue(steps, ComplexValueParameterType.CUSTOM_VARIABLE, id).map((step) => referencedStepMapper(step, stepsList)),
                ...getLiquidsReferencedInComplexValues(liquids, ComplexValueParameterType.CUSTOM_VARIABLE, id).map(({ id, name }) => ({
                    name: name ?? "",
                    type: RecipeDialogType.LIQUID,
                    payload: { isOpen: true, id },
                })),
                ...customVariables
                    .filter(({ formula }) => checkIfFormulaReferencingCustomVariable(formula, id))
                    .map(({ name, id }) => ({
                    name: name ?? "",
                    type: RecipeDialogType.CUSTOM_VARIABLE,
                    payload: { isOpen: true, id },
                })),
            ];
        case RemovableEntityType.RECIPE_LIQUID:
            return getRecipeStepsUsingLiquids(steps, id).map((step) => referencedStepMapper(step, stepsList));
        case RemovableEntityType.RECIPE_GAS:
            return getRecipeStepsUsingGas(steps, id).map((step) => referencedStepMapper(step, stepsList));
        default:
            return [];
    }
});
